import { Tooltip, TruncatedText, type DataTableColumn } from "@carbon6/c6-frontend-design";
import { SessionStatusChip } from "@/components/status/SessionStatusChip";
import { CheckpointCount } from "@/components/table/CheckpointCount";
import { BranchHashCell } from "@/components/table/branchHash";
import monoStyles from "@/components/table/dataTableMono.module.css";
import { DatasetRefCell, TrainingSessionRefLink } from "./trainingRefs";
import { formatRelativeTime } from "@/utils/datetime";
import type { EvalRunTableRow } from "./EvalRunTableRow";
import styles from "./EvaluationResultsPanel.module.css";

export function evaluationResultsEmptyMessage(
  hasFilters: boolean,
  hasPolicyFilter: boolean,
): string {
  if (hasFilters) {
    return "조건에 맞는 Evaluation 결과가 없습니다. Filter 를 지우고 다시 시도해 보세요.";
  }
  if (hasPolicyFilter) {
    return "선택한 Policy 의 Evaluation 실행이 없습니다. Policy 목록에서 Evaluation을 실행해 보세요.";
  }
  return "표시할 Evaluation 결과가 없습니다. Policy 목록에서 Evaluation을 실행해 보세요.";
}

export function buildEvalRunColumns(
  nowMs: number,
  checkpointSortable: boolean,
): readonly DataTableColumn<EvalRunTableRow>[] {
  return [
    {
      key: "policyLabel",
      header: "Policy 이름",
      width: "minmax(180px, 1.4fr)",
      sortable: "none",
      render: (row) => (
        <TrainingSessionRefLink
          sessionId={row.sourceSessionId}
          displayName={row.policyLabel}
          sessionName={row.sessionLabel}
          focus={{ tab: "checkpoints", checkpoint: row.checkpointTag }}
          className={styles.policyText}
        />
      ),
    },
    {
      key: "checkpointLabel",
      header: "#",
      width: "70px",
      align: "center",
      className: styles.num,
      sortable: checkpointSortable ? "both" : "none",
      render: (row) => (
        <CheckpointCount
          value={row.checkpointValue}
          unit={row.budgetUnit}
          sessionTotalEpochs={row.sessionEpochValue}
          sessionTotalSteps={row.sessionStepValue}
        />
      ),
    },
    { key: "unitLabel", header: "UNIT", width: "70px", align: "center" },
    {
      key: "branchHashLabel",
      header: "Branch@Hash",
      width: "minmax(140px, 1fr)",
      sortable: "both",
      render: (row) => <BranchHashCell branch={row.branch} hash={row.shortHash} />,
    },
    {
      key: "datasetLabel",
      header: "Dataset",
      cellPadding: "tight",
      width: "minmax(320px, 2.2fr)",
      sortable: "both",
      render: (row) => (
        <DatasetRefCell registryRef={row.datasetRef} fallbackLabel={row.datasetLabel} />
      ),
    },
    {
      key: "taskLabel",
      header: "Task",
      width: "minmax(160px, 1fr)",
      sortable: "both",
    },
    {
      key: "dockerTagLabel",
      header: "Docker Tag",
      width: "120px",
      sortable: "both",
      render: (row) => (
        <TruncatedText className={monoStyles.mono}>{row.dockerTagLabel}</TruncatedText>
      ),
    },
    {
      key: "startedLabel",
      header: "시작",
      width: "95px",
      sortable: "both",
      render: (row) => (
        <Tooltip content={row.startedAtMs === null ? undefined : row.startedLabel}>
          <span>{formatRelativeTime(row.startedAtMs, nowMs)}</span>
        </Tooltip>
      ),
    },
    {
      key: "durationLabel",
      header: "소요 시간",
      width: "85px",
      className: styles.num,
    },
    {
      key: "seedLabel",
      header: "SEED",
      width: "70px",
      align: "center",
      className: styles.num,
    },
    {
      key: "numEnvsLabel",
      header: "ENVS",
      width: "70px",
      align: "center",
      className: styles.num,
    },
    {
      key: "episodesLabel",
      header: "EPISODES",
      width: "80px",
      align: "center",
      className: styles.num,
    },
    {
      key: "successRateLabel",
      header: "SR[%]",
      width: "80px",
      align: "center",
      className: styles.num,
      sortable: "both",
      render: (row) => (
        <TruncatedText className={styles.srValue}>{row.successRateLabel}</TruncatedText>
      ),
    },
    {
      key: "status",
      header: "상태",
      width: "110px",
      sortable: "both",
      render: (row) => <SessionStatusChip status={row.status} variant="outline" />,
    },
  ];
}
